/**
 * RiskInsightCard — Vertical 360 assessment card
 * Donut chart (left) + Iris findings (right) + qualitative risk band
 */
import React from 'react';
import Dartboard360 from '../../compass/components/boardreports/Dartboard360';
import styles from './RiskInsightCard.module.css';

// Derive posture from sector health — consistent with dartboard colors.
// Dartboard thresholds: >= 7 = high (red), >= 4 = medium (yellow), > 0 = low (green).
function classifyRisk(score, componentData) {
  if (!componentData?.sectors) {
    // Fallback to score-based when no sector data available
    if (score >= 50) return 'elevated';
    if (score >= 25) return 'moderate';
    return 'strong';
  }

  const sectors = Object.values(componentData.sectors);
  let redCount = 0;
  let yellowCount = 0;
  for (const s of sectors) {
    const val = s?.score ?? s?.averageScore ?? 0;
    if (val >= 7) redCount++;
    else if (val >= 4) yellowCount++;
  }
  const total = sectors.length || 1;
  if (redCount / total >= 0.5) return 'elevated';
  if ((redCount + yellowCount) / total >= 0.5) return 'moderate';
  return 'strong';
}

const RISK_LABELS = {
  elevated: 'Elevated Risk',
  moderate: 'Moderate Risk',
  strong: 'Strong Posture',
};

export default function RiskInsightCard({
  title,
  subtitle,
  dartboardTitle,
  score,
  componentData,
  findings = [],
  topAction,
  riskLevelOverride,
  deepDiveLabel,
  deepDiveDisabled = false,
  deepDiveTooltip,
  prismLabel,
  onPrismClick,
  icon: Icon,
  iconColor = '#00d9ff',
}) {
  const riskLevel = riskLevelOverride || classifyRisk(score, componentData);

  return (
    <article className={styles.card}>
      {/* Left — donut chart */}
      <div className={styles.chartColumn}>
        <Dartboard360
          title={dartboardTitle}
          score=""
          componentData={componentData}
          size={180}
          isGrayed={false}
        />
      </div>

      {/* Right — findings */}
      <div className={styles.findingsColumn}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            {Icon && <Icon className={styles.icon} style={{ color: iconColor }} />}
            <h3 className={styles.title}>{title}</h3>
            {subtitle && <span className={styles.subtitle}>· {subtitle}</span>}
          </div>

          <div className={`${styles.riskBand} ${styles[riskLevel]}`}>
            <span className={`${styles.riskIndicator} ${styles[riskLevel]}`} aria-hidden="true" />
            {RISK_LABELS[riskLevel]}
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.findingsLabel}>Key Findings</div>
        <ul className={styles.findingsList}>
          {findings.map((finding, i) => (
            <li key={i} className={styles.findingItem}>
              <span className={styles.findingBullet} aria-hidden="true" />
              <span className={styles.findingText}>{finding}</span>
            </li>
          ))}
        </ul>

        <div className={styles.actionRow}>
          {topAction && (
            <button className={styles.actionLink} type="button">
              <span className={styles.actionArrow}>→</span>
              {topAction}
            </button>
          )}
          <div className={styles.ctaGroup}>
            {prismLabel && (
              <button
                className={styles.prismCta}
                type="button"
                onClick={onPrismClick}
              >
                {prismLabel}
                <span className={styles.prismCtaArrow}>→</span>
              </button>
            )}
            {deepDiveLabel && (
              <button
                className={`${styles.deepDive} ${deepDiveDisabled ? styles.deepDiveDisabled : ''}`}
                type="button"
                disabled={deepDiveDisabled}
                title={deepDiveDisabled ? deepDiveTooltip : undefined}
              >
                {deepDiveLabel}
                {deepDiveDisabled && <span className={styles.comingSoon}>Coming Soon</span>}
                {!deepDiveDisabled && <span className={styles.deepDiveArrow}>→</span>}
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
